import axios from 'axios';
import { createContext, useContext, useEffect, useState } from 'react';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);
    const [userRoles, setUserRoles] = useState([]);
    const [userPermissions, setUserPermissions] = useState([]);

    useEffect(() => {
        // IMPORTANT: Register interceptor BEFORE any requests (like fetchUserRoles)
        const interceptor = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                const isLoginRequest = error.config?.url?.includes('/api/login');

                if (error.response && (error.response.status === 401 || error.response.status === 403) && !isLoginRequest) {
                    const currentUser = localStorage.getItem('user');
                    if (currentUser) {
                        localStorage.setItem('lockedUser', currentUser);
                    }

                    // Clear session data SURGICALLY (don't use clear() to preserve 'rememberedUser')
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    localStorage.removeItem('permissions');

                    delete axios.defaults.headers.common['Authorization'];

                    setToken(null);
                    setUser(null);
                    setUserRoles([]);
                    setUserPermissions([]);

                    // Force redirect to login
                    const reason = error.response.status === 401 ? 'expired' : 'denied';
                    window.location.href = `/login?reason=${reason}`;

                    return Promise.reject(error);
                }
                return Promise.reject(error);
            }
        );

        const initAuth = async () => {
            const savedToken = localStorage.getItem('token');
            const savedUser = localStorage.getItem('user');
            const savedPermissions = localStorage.getItem('permissions');

            if (savedToken && savedUser) {
                const parsedUser = JSON.parse(savedUser);
                setUser(parsedUser);
                setToken(savedToken);
                if (savedPermissions) {
                    setUserPermissions(JSON.parse(savedPermissions));
                }
                // Si ya tenemos roles guardados en el inicio de sesión, usar esos temporalmente para evitar parpadeos
                if (parsedUser.roles) {
                    setUserRoles(parsedUser.roles);
                }

                axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;

                // Fetch de roles actualizados desde la BD y esperar a que lleguen antes de quitar el Loading
                try {
                    const response = await axios.get(`/api/usuarios/${parsedUser.id}/roles`);
                    const freshRoles = response.data.roles || [];
                    setUserRoles(freshRoles);

                    // Actualizar caché
                    parsedUser.roles = freshRoles;
                    localStorage.setItem('user', JSON.stringify(parsedUser));
                } catch (error) {
                    console.error('Error fetching user roles:', error);
                }
            }
            setLoading(false);
        };

        initAuth();

        return () => {
            axios.interceptors.response.eject(interceptor);
        };
    }, []);

    const fetchUserRoles = async (userId) => {
        try {
            const response = await axios.get(`/api/usuarios/${userId}/roles`);
            setUserRoles(response.data.roles || []);
        } catch (error) {
            console.error('Error fetching user roles:', error);
            setUserRoles([]);
        }
    };

    const login = async (identifier, password, portal) => {
        try {
            const response = await axios.post('/api/login', { identifier, password, portal });
            const { user, token } = response.data;

            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));

            // Store permissions if available
            if (user.permissions) {
                localStorage.setItem('permissions', JSON.stringify(user.permissions));
                setUserPermissions(user.permissions);
            }

            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

            setToken(token);
            setUser(user);
            // Roles are also returned in user object now, but we can still fetch them or use from user
            if (user.roles) {
                setUserRoles(user.roles);
            } else {
                await fetchUserRoles(user.id);
            }

            return { success: true };
        } catch (error) {
            console.error('Login failed:', error);
            return {
                success: false,
                error: error.response?.data?.error || 'Login failed'
            };
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('permissions');
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
        setUserRoles([]);
        setUserPermissions([]);
        window.location.href = '/login';
    };

    const hasRole = (roleName) => {
        if (!userRoles) return false;
        // Handle both object array (from DB) and string array (if simplified)
        return userRoles.some(role => {
            const name = typeof role === 'string' ? role : role.nombre;
            return name.toUpperCase() === roleName.toUpperCase();
        });
    };

    const hasPermission = (permissionCode) => {
        if (!userPermissions) return false;
        // Admin always has access (optional, but good practice)
        if (hasRole('ADMIN') || hasRole('ADMINISTRADOR')) return true;

        return userPermissions.includes(permissionCode);
    };

    const updateUser = (updatedUser) => {
        const newUser = { ...user, ...updatedUser };
        setUser(newUser);
        localStorage.setItem('user', JSON.stringify(newUser));
    };

    return (
        <AuthContext.Provider value={{ user, token, userRoles, userPermissions, hasRole, hasPermission, login, logout, updateUser, loading }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
